import numeral from 'numeral'
import { ComplianceOverviewUrl } from 'constants/RouteUrls'
import Button from 'core/components/buttons/Button'
import Drawer from 'core/components/drawer/Drawer'
import useModal from 'core/hooks/useModal'
import useNavigation from 'core/hooks/useNavigation'
import ComplianceStatus from 'core/widgets/ComplianceStatus'
import { getRejectionReasonsContent } from 'pages/teams/compliance/helpers'
import { TaxReport } from 'types/entity'
import { tableCellFormat } from 'utils/formatter'
import { toCurrency } from 'utils/Functions'

interface MonthlyBreakdownDrawerProps {
  year: number
  quarter: string
  quarterlyData: TaxReport['by_month'][string][]
}

const months_by_quarter: { [key: string]: string[] } = {
  '1': ['January', 'February', 'March'],
  '2': ['April', 'May', 'June'],
  '3': ['July', 'August', 'September'],
  '4': ['October', 'November', 'December']
}

const MonthlyBreakdownDrawer = ({
  year,
  quarter,
  quarterlyData
}: MonthlyBreakdownDrawerProps) => {
  const { push } = useNavigation()
  const { open, openModal, closeModal } = useModal()

  return (
    <>
      <Button
        variant="link"
        className="text-primary"
        onClick={() => {
          openModal()
        }}
      >
        View monthly breakdown
      </Button>
      <Drawer startFrom="right" show={open} onClose={closeModal} width="md">
        <h1 className="mt-0">{`Quarter ${quarter}, ${year}`}</h1>
        {quarterlyData.map((monthData, index) => {
          const {
            verification_errors,
            document_verification_status,
            vehicle_verification_status,
            monthly_required_mileage,
            monthly_annualized_mileage,
            mileage_verification_status
          } = monthData
          const { vehicleFieldsReasons, documentFieldsReasons } =
            getRejectionReasonsContent(verification_errors ?? {})
          const mileageReasons: string[] = [
            `Annualized mileage: ${numeral(monthly_required_mileage).format('0,0')} mi/month`,
            `Mileage requirement: ${numeral(monthly_annualized_mileage).format('0,0')} mi/month`
          ]

          return (
            <div key={index} className="card mt-4 overflow-auto max-w-3xl">
              <h3 className="text-xl font-medium mb-5">
                {months_by_quarter[quarter][index]} {year}
              </h3>
              <div className="grid grid-cols-[176px_176px_auto] gap-6 mb-6">
                <div className="max-w-52">
                  <p className="text-xl font-medium mb-2">
                    {tableCellFormat(
                      monthData.approved_mileage_while_non_compliant,
                      'miles'
                    )}
                  </p>
                  <p className="text-dark-4">
                    Approved mileage while non-compliant
                  </p>
                </div>
                <div>
                  <p className="text-xl font-medium mb-2">
                    {toCurrency(
                      monthData.payments_received_while_non_compliant
                    )}
                  </p>
                  <p className="text-dark-4">
                    Payments received while non-compliant
                  </p>
                </div>
                <div>
                  <p className="text-xl font-medium mb-2">
                    {monthData.program.name ?? 'N/A'}
                  </p>
                  <p className="text-dark-4">
                    {monthData.program.program_type
                      ? `${monthData.program.program_type?.toUpperCase()} Program`
                      : 'No program subject to tax'}
                  </p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex flex-col gap-2">
                  <ComplianceStatus
                    status={document_verification_status}
                    reasons={documentFieldsReasons}
                  />
                  <p className="text-dark-4">Document compliance</p>
                </div>
                <div className="flex flex-col gap-2">
                  <ComplianceStatus
                    status={vehicle_verification_status}
                    reasons={vehicleFieldsReasons}
                  />
                  <p className="text-dark-4">Vehicle compliance</p>
                </div>
                <div className="flex flex-col gap-2">
                  <ComplianceStatus
                    status={mileage_verification_status}
                    reasons={mileageReasons}
                    footer={
                      <div>
                        <Button
                          variant="link"
                          className="underline"
                          onClick={() => {
                            push(ComplianceOverviewUrl)
                          }}
                        >
                          Go to compliance
                        </Button>
                      </div>
                    }
                  />
                  <p className="text-dark-4">Mileage compliance</p>
                </div>
              </div>
            </div>
          )
        })}
      </Drawer>
    </>
  )
}

export default MonthlyBreakdownDrawer
